import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  dateToMidnightUTCString,
  mapRepliconDateToUtcObject
} from '~/modules/common/dates/convert';
import { ResourceRequestStatus } from '~/types';
import { roundToDecimals } from '~/modules/resourcing/common/util';

import { useResourceUserAvailabilityHoursSeriesForDateRange } from '~/modules/resourcing/common/hooks';
import getAllocationPeriodEditorDateRange from '../ResourceAllocationPeriodEditPopover/components/ResourceAllocationEditor/getAllocationPeriodEditorDateRange';
import getResourceAllocation, {
  getResourceAllocationWithAllocationSeriesData
} from '../ResourceAllocationPeriodEditPopover/getResourceAllocation';
import ResourceAllocationPeriodEditPopover from '../ResourceAllocationPeriodEditPopover';
import useStyles from './useStyles';

const ResourceAllocationPeriodEditPopoverDetails = ({
  classes: classesOverrides,
  allocationPeriodEditDetails,
  resourceRequest,
  handleAllocationPeriodClose,
  chartStartDate,
  chartEndDate,
  setNextPeriod,
  onAllocationChange: allocChange,
  isAllocationComplete = false,
  setPreviousPeriod
}) => {
  const {
    anchorEl,
    resourceAllocation: {
      user: { userUri, scheduleDurationByDay },
      scheduleRules
    },
    allocatedHours: allocHours
  } = allocationPeriodEditDetails;
  const allocatedHours = roundToDecimals(allocHours);

  const classes = useStyles({ classes: classesOverrides });

  const isStatusMatched = useMemo(
    () => statuses => statuses.some(s => s === resourceRequest?.requestStatus),
    [resourceRequest]
  );

  const popoverClasses = useMemo(
    () => ({
      paper: classNames(classes.root, {
        [classes.rootSubmitted]: isStatusMatched([
          ResourceRequestStatus.Submitted,
          ResourceRequestStatus.Allocationrejected
        ]),
        [classes.rootTentative]: isStatusMatched([
          ResourceRequestStatus.Tentative
        ]),
        [classes.rootComplete]: isStatusMatched([
          ResourceRequestStatus.Complete
        ])
      })
    }),
    [
      classes.root,
      classes.rootSubmitted,
      classes.rootTentative,
      classes.rootComplete,
      isStatusMatched
    ]
  );

  const { periodStartDate, periodEndDate } = getAllocationPeriodEditorDateRange(
    {
      periodDetails: allocationPeriodEditDetails
    }
  );

  const onAllocationChange = useCallback(
    evt => {
      const originalAllocationWithPeriod = {
        ...allocationPeriodEditDetails.resourceAllocation,
        periodDetails: {
          startDate: periodStartDate,
          endDate: periodEndDate
        }
      };

      allocChange(evt, originalAllocationWithPeriod);
    },
    [
      allocChange,
      allocationPeriodEditDetails.resourceAllocation,
      periodEndDate,
      periodStartDate
    ]
  );

  const {
    isLoading,
    availabilityHoursSeriesByDay: hourSeriesByDay
  } = useResourceUserAvailabilityHoursSeriesForDateRange({
    userUri,
    dateRange: {
      startDate: dateToMidnightUTCString(chartStartDate),
      endDate: dateToMidnightUTCString(chartEndDate)
    }
  });

  const scheduledHours =
    (scheduleDurationByDay || [])
      .filter(d => {
        const date = mapRepliconDateToUtcObject(d.date);

        return date >= periodStartDate && date <= periodEndDate;
      })
      .reduce((acc, curr) => acc + curr.hours, 0) || 0;

  const availabilityHoursSeriesByDay = (hourSeriesByDay || []).filter(
    series => series.date >= periodStartDate && series.date <= periodEndDate
  );

  const availableHours =
    (!isLoading &&
      roundToDecimals(
        availabilityHoursSeriesByDay.reduce((acc, curr) => curr.hours + acc, 0)
      ) + (isAllocationComplete ? allocatedHours : 0)) ||
    0;

  const useCreateAllocation = useCallback(
    ({ allocation, newAllocatedHours, resourceAllocationSeriesData }) => {
      const { alloc } =
        isAllocationComplete && availableHours - allocatedHours === 0
          ? getResourceAllocationWithAllocationSeriesData({
              resourceAllocationSeriesData,
              scheduleRules,
              allocation,
              newAllocatedHours
            })
          : getResourceAllocation({
              allocation,
              newAllocatedHours,
              availabilityHoursSeriesByDay
            });

      return alloc;
    },
    [
      isAllocationComplete,
      availableHours,
      allocatedHours,
      scheduleRules,
      availabilityHoursSeriesByDay
    ]
  );

  return (
    <ResourceAllocationPeriodEditPopover
      anchorEl={anchorEl}
      isDataLoading={isLoading}
      periodDetails={allocationPeriodEditDetails}
      allocatedHours={allocatedHours}
      useCreateAllocation={useCreateAllocation}
      popoverClasses={popoverClasses}
      handleAllocationPeriodClose={handleAllocationPeriodClose}
      setNextPeriod={setNextPeriod}
      scheduledHours={scheduledHours}
      availableHours={availableHours}
      isAllocationComplete={isAllocationComplete}
      onAllocationChange={onAllocationChange}
      setPreviousPeriod={setPreviousPeriod}
    />
  );
};

ResourceAllocationPeriodEditPopoverDetails.propTypes = {
  classes: PropTypes.object,
  onAllocationChange: PropTypes.func.isRequired,
  allocationPeriodEditDetails: PropTypes.object,
  resourceRequest: PropTypes.object,
  handleAllocationPeriodClose: PropTypes.func,
  setNextPeriod: PropTypes.func,
  isAllocationComplete: PropTypes.bool,
  setPreviousPeriod: PropTypes.func,
  chartStartDate: PropTypes.object,
  chartEndDate: PropTypes.object
};

export default ResourceAllocationPeriodEditPopoverDetails;
